import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { readFragmentShim } from './fragmentCacheOperations.ts';
import { toDocumentNode } from './toDocumentNode.ts';

export type ReadFragmentInteropOptions<TVariables extends Record<string, any>> = {
	id?: string;
	fragment: GraphQLTaggedNode;
	variables?: TVariables;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseReadFragmentInteropArgs = {
	id?: string;
	fragmentSpec: GraphQLTaggedNode;
	variables?: Record<string, any>;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseReadFragmentInteropResult<TData> = (params?: {
	id?: string;
	variables?: Record<string, any>;
}) => TData | null;

export type UseReadFragmentInteropHook<TData> = (
	params: UseReadFragmentInteropArgs,
) => UseReadFragmentInteropResult<TData>;

/**
 * Imperative read fragment with the same options shape as Apollo's readFragment.
 * Uses BUILD_VARIANT to call either the Relay fragment shim or Apollo client readFragment.
 */
export function readFragmentInterop<TData, TVariables extends Record<string, any> = {}>(
	options: ReadFragmentInteropOptions<TVariables>,
): TData | null {
	const { id, fragment, variables, interopApolloAggClient, __callerPackage__ } = options;
	if (!id) {
		return null;
	}

	if (isRelayEnabled(__callerPackage__)) {
		const env = window['GLOBAL_RELAY_ENV']!;
		return readFragmentShim<TData>(env, fragment, id, (variables ?? {}) as Record<string, any>);
	}

	const client = interopApolloAggClient
		? window['GLOBAL_APOLLO_AGG_CLIENT']
		: window['GLOBAL_APOLLO_CLIENT'];

	const data = client!.readFragment<TData>({
		id,
		fragment: toDocumentNode(fragment),
		variables: variables ?? {},
	});

	return data as TData;
}

function useReadFragmentInteropRelay<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec, id: defaultId, variables: defaultVariables } = params;
	const env = useRelayEnvironment();

	return useCallback(
		(callbackParams?: { id?: string; variables?: Record<string, any> }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			const variables = callbackParams?.variables ?? defaultVariables ?? {};

			// Reads stay native to the selected route; bridge sync happens on
			// writes/successful results, not cache reads.
			return readFragmentShim<TData>(env, fragmentSpec, id, variables);
		},
		[env, fragmentSpec, defaultId, defaultVariables],
	);
}

function useReadFragmentInteropApollo<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec: fragment, id: defaultId, variables: defaultVariables } = params;

	const client = useApolloClient();

	return useCallback(
		(callbackParams?: { id?: string; variables?: Record<string, any> }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			const variables = callbackParams?.variables ?? defaultVariables ?? {};

			// Reads stay native to the selected route; bridge sync happens on
			// writes/successful results, not cache reads.
			const data = client.readFragment<TData>({
				id,
				fragment: toDocumentNode(fragment),
				variables,
			});

			return data as TData;
		},
		[client, fragment, defaultId, defaultVariables],
	);
}

export function useReadFragmentInterop<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	return isRelayEnabled(params.__callerPackage__)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropRelay(params)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropApollo(params);
}
